import React, { useRef, useCallback } from 'react';
import axios from 'axios';
import Button from 'react-bootstrap/Button';
import { Container, InputGroup, Form } from 'react-bootstrap';
import Testimonials from './Testimonials';
import { useState } from 'react';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { useLanguage } from '../contexts/LanguageContext';

/* ─── Email typo suggestion helpers ─── */
// Ordered by popularity — index is used as tiebreaker (lower = preferred)
const COMMON_DOMAINS = [
  'gmail.com',        // 0  — dominant
  'yahoo.com',        // 1
  'outlook.com',      // 2
  'hotmail.com',      // 3
  'icloud.com',       // 4
  'aol.com',          // 5
  'live.com',         // 6
  'msn.com',          // 7
  'protonmail.com',   // 8
  'mail.com',         // 9
  'zoho.com',         // 10
  'yandex.com',       // 11
  'comcast.net',      // 12
  'att.net',          // 13
  'verizon.net',      // 14
  'me.com',           // 15
  'mac.com',          // 16
  'gmx.com',          // 17
  'sbcglobal.net',    // 18
  'cox.net',          // 19
  'fastmail.com',     // 20
  'hey.com',          // 21
];

const VALID_TLDS = [
  'com', 'net', 'org', 'edu', 'gov', 'io', 'co', 'us', 'uk', 'ca',
  'de', 'fr', 'au', 'in', 'jp', 'br', 'it', 'es', 'nl', 'ru',
  'se', 'no', 'fi', 'dk', 'pl', 'cz', 'at', 'ch', 'be', 'ie',
  'nz', 'za', 'mx', 'ar', 'cl', 'pt', 'info', 'biz', 'me', 'tv',
  'app', 'dev', 'xyz',
];

const TLD_TYPOS = {
  'con': 'com', 'cmo': 'com', 'ocm': 'com', 'cm': 'com', 'vom': 'com',
  'dom': 'com', 'om': 'com', 'comm': 'com', 'coom': 'com', 'clm': 'com',
  'ner': 'net', 'nte': 'net', 'met': 'net', 'ne': 'net', 'nett': 'net',
  'ogr': 'org', 'rog': 'org', 'or': 'org', 'orgg': 'org',
  'edi': 'edu', 'ed': 'edu',
};

const levenshtein = (a, b) => {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
  return dp[m][n];
};

const suggestEmail = (email) => {
  if (!email || !email.includes('@')) return null;

  const [local, domain] = email.toLowerCase().trim().split('@');
  if (!local || !domain || !domain.includes('.')) return null;

  const parts = domain.split('.');
  const tld = parts[parts.length - 1];
  const domainName = parts.slice(0, -1).join('.');

  // If domain is already a known valid domain, nothing to fix
  if (COMMON_DOMAINS.includes(domain)) return null;

  // Fix obvious TLD typos first
  let fixedTld = TLD_TYPOS[tld] || tld;
  let fixedDomain = domainName + '.' + fixedTld;

  // If TLD was fixed and domain is now known, suggest that
  if (fixedTld !== tld && COMMON_DOMAINS.includes(fixedDomain)) {
    return local + '@' + fixedDomain;
  }

  // Find all known domains within edit distance 3
  const candidates = [];
  for (let i = 0; i < COMMON_DOMAINS.length; i++) {
    const known = COMMON_DOMAINS[i];
    const dist = levenshtein(fixedDomain, known);
    if (dist > 0 && dist <= 3) {
      candidates.push({ domain: known, dist, rank: i });
    }
  }

  // Pick the best candidate:
  // 1. If a top-5 domain (gmail, yahoo, outlook, hotmail, icloud) is within dist 3, prefer it
  // 2. Otherwise pick the lowest distance, breaking ties by popularity rank
  let bestMatch = null;
  if (candidates.length > 0) {
    candidates.sort((a, b) => {
      const aTop = a.rank <= 4 ? 1 : 0;
      const bTop = b.rank <= 4 ? 1 : 0;
      // If one is a top domain and within dist 3, and the other isn't, prefer the top one
      if (aTop !== bTop) return bTop - aTop;
      // Same tier — prefer lower distance
      if (a.dist !== b.dist) return a.dist - b.dist;
      // Same distance — prefer more popular
      return a.rank - b.rank;
    });
    const best = candidates[0];
    // Cap: top-5 domains allow dist 3, others max dist 2
    if (best.dist <= (best.rank <= 4 ? 3 : 2)) {
      bestMatch = best.domain;
    }
  }

  // No close match found — only suggest if TLD itself was fixed
  if (!bestMatch) {
    if (fixedTld !== tld) {
      return local + '@' + fixedDomain;
    }
    return null;
  }

  const suggested = bestMatch;
  const full = local + '@' + suggested;

  // Only suggest if something actually changed
  if (full === email.toLowerCase().trim()) return null;

  // Validate the suggested TLD is real
  const suggestedTld = suggested.split('.').pop();
  if (!VALID_TLDS.includes(suggestedTld)) return null;

  return full;
};

// Renamed component to match the filename. 
// Destructure the onStepComplete prop directly:
const RegisterProfile = ({ complete }) => {
  const L = useLanguage();
  const emailRef = useRef();

  const passwordRef = useRef();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailSuggestion, setEmailSuggestion] = useState(null);
  const debounceRef = useRef(null);

  const handleEmailChange = (value) => {
    setEmail(value);
    setEmailSuggestion(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setEmailSuggestion(suggestEmail(value));
    }, 800);
  };

  const applySuggestion = () => {
    setEmail(emailSuggestion);
    setEmailSuggestion(null);
  };

  // This function is called when the form is submitted
  const handleButtonClick = (e) => {
    e.preventDefault();
    complete(email,  password);
  };

  return (
    <div>
      <Container className="bg-white shadow-lg rounded p-4 my-12 justify-content-center text-center max-w-lg" fluid>

        <Form onSubmit={handleButtonClick} autoComplete="off">
          <h1 className="font-bold mb-1 text-instagram stripe-heading stripe-heading--lg">{L.registerProfile.heading}</h1>
          <small className="stripe-text-muted mb-3 d-block">
            {L.registerProfile.subheading}
          </small>

          {/* Email */}
          <InputGroup className={emailSuggestion ? 'mb-1' : 'mb-3'} size="md">
            <InputGroup.Text id="instagram-addon" className="py-3">
              <i className="fa-solid fa-envelope" />
            </InputGroup.Text>
            <Form.Control
              type="email"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              placeholder={L.registerProfile.emailPlaceholder}
              aria-label={L.registerProfile.emailAriaLabel}
              aria-describedby="email-addon"
              autoComplete="off"
            />
          </InputGroup>

          {/* Typo suggestion */}
          {emailSuggestion && (
            <div
              onClick={applySuggestion}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: '8px 14px',
                marginBottom: 12,
                borderRadius: 8,
                background: '#fef9ec',
                border: '1px solid #fde68a',
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#fef3c7'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#fef9ec'}
            >
              <i className="fa-solid fa-wand-magic-sparkles" style={{ color: '#d97706', fontSize: '0.8rem' }}></i>
              <span style={{ fontSize: '0.85rem', color: '#92400e' }}>
                {L.registerProfile.didYouMean(emailSuggestion)}
              </span>
            </div>
          )}

		  {/* <InputGroup className="mb-3" size="md">
            <InputGroup.Text id="instagram-addon" className="py-3">
              <i className="fa-solid fa-lock" />
            </InputGroup.Text>
            <Form.Control
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              aria-label="Password"
              aria-describedby="password-addon"
              autoComplete="new-password"
            />
          </InputGroup> */}

			<Button variant="primary" type="submit" className="w-full py-2 stripe-btn stripe-btn--primary">
				{L.registerProfile.continue}
			</Button>
		  {/* <br></br> <br></br>
		  <center>
		  <GoogleOAuthProvider clientId="255966676242-fhjo5pkp2eo7argo8oghm2u5u16253uk.apps.googleusercontent.com" data-text="signup_with">
				<GoogleLogin
					onSuccess={complete}
					data-text="signup_with"
					text="signup_with"
				/>
			</GoogleOAuthProvider>
			</center> */}
        </Form>

        <small className="d-block stripe-text-muted mt-6">
          {L.registerProfile.alreadyHaveAccount}
        </small>
      </Container>
	
    </div>
  );
};

export default RegisterProfile;
